// U4 "Draft on approach": as the owner selects a card that needs a reply (or it is next in focus
// mode), ask the server to write its draft ahead, once per revision of the card. The server
// dedupes too, and says "paused" under the spend cap, which the card then shows. `askForDraft` is
// the owner's own ask (`e` on a card without a draft, or an answer to the card's question).
import { useEffect, useSyncExternalStore } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { CardSummary, DraftRequestStatus } from "@shared/api";
import { needsReply } from "@shared/drafts";
import { isApiError } from "@web/lib/api";
import { feedApi } from "@web/lib/feedApi";
import { acceptCard } from "@web/lib/feedQueries";
import { isToReview } from "../selectors";

const statuses = new Map<string, DraftRequestStatus>();
const asked = new Set<string>();
const listeners = new Set<() => void>();

function setStatus(cardId: string, status: DraftRequestStatus): void {
  if (statuses.get(cardId) === status) return;
  statuses.set(cardId, status);
  for (const l of listeners) l();
}

/** The last answer the server gave about drafting this card (null before any ask). */
export function useDraftRequest(cardId: string): DraftRequestStatus | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => statuses.get(cardId) ?? null,
    () => null,
  );
}

type Approached = Pick<CardSummary, "id" | "rev" | "kind" | "ask" | "status">;

/** Asks for a draft ahead when the card needs a reply; at most once per card revision. */
export function requestDraftAhead(queryClient: QueryClient, card: Approached): void {
  if (!needsReply(card) || !isToReview(card)) return;
  const key = `${card.id}@${card.rev}`;
  if (asked.has(key)) return;
  asked.add(key);
  feedApi.draftAhead(card.id).then(
    (res) => {
      setStatus(card.id, res.status);
      if (res.status === "queued" || res.status === "working") acceptCard(queryClient, res.card);
    },
    (error) => {
      // Not wired (no operator): the card offers `e` instead. Anything else may try again later.
      if (isApiError(error, "not_available")) setStatus(card.id, "no_model");
      else asked.delete(key);
    },
  );
}

/** Draft ahead while `enabled` (the card is selected, or next in focus mode). */
export function useDraftAhead(card: Approached | null | undefined, enabled: boolean): void {
  const queryClient = useQueryClient();
  const id = card?.id;
  const rev = card?.rev;
  useEffect(() => {
    if (enabled && card) requestDraftAhead(queryClient, card);
    // The card's id and revision decide; the rest of the summary is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, id, rev, queryClient]);
}

const OWNER_WORDS: Partial<Record<DraftRequestStatus, string>> = {
  busy: "Claude is already working on this card. Ask again once it's done.",
  no_model: "Claude isn't connected, so it can't draft this one.",
  not_needed: "This card isn't waiting for a reply from you.",
  ready: "This card already has a draft.",
};

/** The owner asks for a draft: `e` without one, or an answer to the card's question. */
export async function askForDraft(
  queryClient: QueryClient,
  cardId: string,
  answer?: string,
): Promise<boolean> {
  try {
    const res = await feedApi.draft(cardId, answer);
    setStatus(cardId, res.status);
    acceptCard(queryClient, res.card);
    const words = OWNER_WORDS[res.status];
    if (words) toast(words);
    return res.status === "queued" || res.status === "working";
  } catch (error) {
    toast(
      isApiError(error, "not_available")
        ? "Claude isn't connected, so it can't draft this one."
        : "Couldn't ask for a draft just now. Nothing changed.",
    );
    return false;
  }
}
